<script setup lang="ts">
definePageMeta({ prerender: true })

const { t } = useI18n()

const { data: home } = await useAsyncData('home', () => queryCollection('home').first())

useSeoMeta({
  description: home.value?.hero.description,
  ogDescription: home.value?.hero.description,
  title: home.value?.hero.title,
})

const availableGames = computed(() =>
  (home.value?.games ?? [])
    .filter(g => g.status === 'available')
    .sort((a, b) => (a.order ?? 99) - (b.order ?? 99)),
)

const comingSoonGames = computed(() =>
  (home.value?.games ?? [])
    .filter(g => g.status === 'coming-soon')
    .sort((a, b) => (a.order ?? 99) - (b.order ?? 99)),
)
</script>

<template>
  <div v-if="home">
    <!-- ── Hero ─────────────────────────────────────────────────── -->
    <UPageHero
      :description="home.hero.description"
      :links="home.hero.links"
    >
      <template #headline>
        <NuxtImg
          src="/images/sfg-logo.png"
          alt="Signal Fire Games — Interactive, web-based games for Christian youth groups"
          width="600"
          height="600"
          format="webp"
          quality="90"
          class="mx-auto w-full max-w-xl object-contain"
        />
      </template>
    </UPageHero>

    <!-- ── Mission Features ──────────────────────────────────────────── -->
    <UPageSection
      id="mission"
      :description="home.mission.description"
      :features="home.mission.features"
      :title="home.mission.title"
    />

    <!-- ── Games Hub ─────────────────────────────────────────────────── -->
    <UPageSection
      id="games"
      :description="home.gamesSection.description"
      :title="home.gamesSection.title"
    >
      <!-- Available games grid -->
      <UPageGrid v-if="availableGames.length > 0">
        <GameCard
          v-for="game in availableGames"
          :key="game.title"
          :game="game"
        />
      </UPageGrid>

      <!-- Coming Soon grid -->
      <template v-if="comingSoonGames.length > 0">
        <p
          v-if="availableGames.length > 0"
          class="mt-8 mb-4 text-sm font-semibold text-muted uppercase tracking-wider"
        >
          {{ t('gamesSection.comingSoonSubheading') }}
        </p>
        <UPageGrid>
          <GameCard
            v-for="game in comingSoonGames"
            :key="game.title"
            :game="game"
          />
        </UPageGrid>
      </template>

      <!-- Empty state -->
      <div
        v-if="(home?.games?.length ?? 0) === 0"
        class="flex flex-col items-center gap-4 py-16 text-center"
      >
        <UIcon
          name="mdi:fire"
          class="text-primary size-16"
        />
        <p class="text-lg text-muted">
          {{ t('gamesSection.emptyMessage') }}
        </p>
      </div>
    </UPageSection>

    <!-- ── CTA ───────────────────────────────────────────────────────── -->
    <UPageSection>
      <UPageCTA
        :description="home.cta.description"
        :links="home.cta.links"
        :title="home.cta.title"
        variant="subtle"
      />
    </UPageSection>
  </div>
</template>
